import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

// Keyword data with intent and difficulty
export interface KeywordData {
  keyword: string;
  intent: "informational" | "transactional" | "navigational" | "commercial";
  difficulty: "low" | "medium" | "high";
  volumeEstimate: "low" | "medium" | "high";
  priority: number;
}

// On-page SEO suggestions
export interface OnPageSuggestions {
  titleSuggestions: string[];
  metaDescriptions: string[];
  headerStructure: HeaderNode[];
  schemaRecommendations: SchemaRecommendation[];
  internalLinkSuggestions: string[];
}

export interface HeaderNode {
  level: "h1" | "h2" | "h3";
  text: string;
  keywords?: string[];
}

export interface SchemaRecommendation {
  type: "FAQ" | "HowTo" | "Article" | "LocalBusiness" | "Product" | "Review";
  reason: string;
  priority: "high" | "medium" | "low";
}

// Content brief for writers
export interface ContentBrief {
  id: string;
  targetKeyword: string;
  secondaryKeywords: string[];
  suggestedWordCount: number;
  outline: OutlineSection[];
  keyPointsToCover: string[];
  competitorInsights: string[];
  internalLinkSuggestions: string[];
  externalLinkSuggestions: string[];
  tone: string;
  targetAudience: string;
  createdAt: string;
}

export interface OutlineSection {
  heading: string;
  level: "h2" | "h3";
  keyPoints: string[];
  suggestedWordCount?: number;
}

// Technical SEO checklist
export interface TechnicalChecklistItem {
  category: "speed" | "mobile" | "structure" | "indexing" | "security";
  item: string;
  description: string;
  priority: "high" | "medium" | "low";
  status?: "pass" | "fail" | "warning" | "unchecked";
}

// Complete SEO analysis result
export interface SEOAnalysis {
  keywords: {
    primary: KeywordData[];
    longTail: KeywordData[];
    questions: string[];
    gaps: string[];
  };
  onPage: OnPageSuggestions;
  contentBriefs: ContentBrief[];
  technicalChecklist: TechnicalChecklistItem[];
  generatedAt?: string;
}

interface UseSEOAgentResult {
  isLoading: boolean;
  isGeneratingBrief: boolean;
  error: string | null;
  analysis: SEOAnalysis | null;
  currentBrief: ContentBrief | null;
  generateAnalysis: (projectId: string) => Promise<void>;
  generateContentBrief: (projectId: string, keyword: string) => Promise<ContentBrief | null>;
  generateOnPageSuggestions: (projectId: string, url?: string) => Promise<OnPageSuggestions | null>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-powered SEO analysis and content brief generation
 */
export function useSEOAgent(): UseSEOAgentResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<SEOAnalysis | null>(null);
  const [currentBrief, setCurrentBrief] = useState<ContentBrief | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setAnalysis(null);
    setCurrentBrief(null);
  }, []);

  /**
   * Generate comprehensive SEO analysis for a project
   */
  const generateAnalysis = useCallback(async (projectId: string): Promise<void> => {
    if (!projectId) {
      setError("Project ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const generateFunction = httpsCallable<
        { projectId: string },
        { success: boolean; analysis: SEOAnalysis }
      >(functions, "generateSEOAnalysis");

      const result = await generateFunction({ projectId });
      setAnalysis(result.data.analysis);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to generate SEO analysis";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Generate a content brief for a specific keyword
   */
  const generateContentBrief = useCallback(
    async (projectId: string, keyword: string): Promise<ContentBrief | null> => {
      if (!projectId || !keyword) {
        setError("Project ID and keyword are required");
        return null;
      }

      setIsGeneratingBrief(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          { projectId: string; keyword: string },
          { success: boolean; brief: ContentBrief }
        >(functions, "generateContentBrief");

        const result = await generateFunction({ projectId, keyword });
        setCurrentBrief(result.data.brief);

        // Also add to analysis briefs if analysis exists
        if (analysis) {
          setAnalysis({
            ...analysis,
            contentBriefs: [...analysis.contentBriefs, result.data.brief],
          });
        }

        return result.data.brief;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate content brief";
        setError(errorMessage);
        return null;
      } finally {
        setIsGeneratingBrief(false);
      }
    },
    [analysis]
  );

  /**
   * Generate on-page SEO suggestions
   */
  const generateOnPageSuggestions = useCallback(
    async (projectId: string, url?: string): Promise<OnPageSuggestions | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          { projectId: string; url?: string },
          { success: boolean; suggestions: OnPageSuggestions }
        >(functions, "generateOnPageSuggestions");

        const result = await generateFunction({ projectId, url });

        // Update analysis if it exists
        if (analysis) {
          setAnalysis({
            ...analysis,
            onPage: result.data.suggestions,
          });
        }

        return result.data.suggestions;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate on-page suggestions";
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [analysis]
  );

  return {
    isLoading,
    isGeneratingBrief,
    error,
    analysis,
    currentBrief,
    generateAnalysis,
    generateContentBrief,
    generateOnPageSuggestions,
    clearError,
    reset,
  };
}
